import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit, Trash2, Eye, EyeOff, X, Check } from 'lucide-react';

const GestionUsuarios = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [filteredUsuarios, setFilteredUsuarios] = useState([]);
  const [roles, setRoles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    usuario: '',
    nombre_apellidos: '',
    email: '',
    password: '',
    id_rol: ''
  });

  const [formErrors, setFormErrors] = useState({});

  // Fetch usuarios and roles on mount
  useEffect(() => {
    fetchUsuarios();
    fetchRoles();
  }, []);

  // Filter usuarios by search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredUsuarios(usuarios);
    } else {
      const filtered = usuarios.filter(u =>
        u.usuario?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.nombre_apellidos?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.rol?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsuarios(filtered);
    }
  }, [searchTerm, usuarios]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  };

  const fetchUsuarios = async () => {
    setLoading(true);
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';
      const response = await fetch(`${API_URL}/admin/usuarios?limit=500`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      setUsuarios(data.usuarios || []);
      setFilteredUsuarios(data.usuarios || []);
    } catch (err) {
      console.error('Error fetching usuarios:', err);
      setError('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';
      const response = await fetch(`${API_URL}/admin/roles`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      setRoles(data.roles || []);
    } catch (err) {
      console.error('Error fetching roles:', err);
    }
  };

  const handleOpenModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        usuario: user.usuario,
        nombre_apellidos: user.nombre_apellidos,
        email: user.email || '',
        password: '', // Don't populate password on edit
        id_rol: user.id_rol
      });
    } else {
      setEditingUser(null);
      setFormData({
        usuario: '',
        nombre_apellidos: '',
        email: '',
        password: '',
        id_rol: ''
      });
    }
    setFormErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({ usuario: '', nombre_apellidos: '', email: '', password: '', id_rol: '' });
    setFormErrors({});
    setShowPassword(false);
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.usuario.trim()) {
      errors.usuario = 'Usuario es requerido';
    }
    if (!formData.nombre_apellidos.trim()) {
      errors.nombre_apellidos = 'Nombre y apellidos son requeridos';
    }
    if (!formData.email.trim()) {
      errors.email = 'Correo electrónico es requerido';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Correo electrónico inválido';
    }
    if (!editingUser && !formData.password) {
      errors.password = 'Contraseña es requerida';
    }
    if (formData.password && formData.password.length < 8) {
      errors.password = 'La contraseña debe tener al menos 8 caracteres';
    }
    if (!formData.id_rol) {
      errors.id_rol = 'Rol es requerido';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';
      const url = editingUser
        ? `${API_URL}/admin/usuarios/${editingUser.id_usuario}`
        : `${API_URL}/admin/usuarios`;

      const method = editingUser ? 'PUT' : 'POST';

      const payload = { ...formData };
      if (editingUser && !payload.password) {
        delete payload.password;
      }

      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Error al guardar usuario');
      }

      await fetchUsuarios();
      handleCloseModal();
    } catch (err) {
      setError(err.message || 'Error al guardar usuario');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEstado = async (usuario) => {
    if (!window.confirm(`¿Estás seguro de ${usuario.estado === 'activo' ? 'inhabilitar' : 'habilitar'} este usuario?`)) {
      return;
    }

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';
      const response = await fetch(`${API_URL}/admin/usuarios/${usuario.id_usuario}/estado`, {
        method: 'PATCH',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Error al cambiar estado');
      }

      await fetchUsuarios();
    } catch (err) {
      alert(err.message || 'Error al cambiar estado del usuario');
    }
  };

  const handleDelete = async (usuario) => {
    if (!window.confirm(`¿Estás seguro de eliminar el usuario "${usuario.usuario}"?`)) {
      return;
    }

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';
      const response = await fetch(`${API_URL}/admin/usuarios/${usuario.id_usuario}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Error al eliminar usuario');
      }

      await fetchUsuarios();
    } catch (err) {
      alert(err.message || 'Error al eliminar usuario');
    }
  };

  const formatFecha = (fecha) => {
    if (!fecha) return 'N/A';
    const date = new Date(fecha);
    return date.toLocaleString('es-BO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-4 md:p-6">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-800">
          Administrador &gt; Gestión de Usuarios
        </h1>
        <p className="text-sm text-gray-600 mt-1">Administra los usuarios del sistema</p>
      </div>

      {/* Toolbar con diseño unificado */}
      <div className="mb-6 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-grow flex items-end gap-2 flex-wrap md:flex-nowrap">
            <div className="relative flex-grow">
              <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">BUSCAR</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="text"
                  placeholder="Buscar por usuario, nombre, correo o rol..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 hover:border-blue-500"
                />
              </div>
            </div>
            <button
              onClick={() => handleOpenModal()}
              className="flex items-center gap-2 bg-[#1e3a8a] text-white px-6 py-2 rounded-full hover:bg-[#2c4a9a] transition text-sm whitespace-nowrap"
            >
              <Plus size={18} />
              <span>Añadir Usuario</span>
            </button>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
            {error}
          </div>
        )}

        {loading && !usuarios.length ? (
          <div className="text-center py-8 text-gray-500">Cargando usuarios...</div>
        ) : filteredUsuarios.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {searchTerm ? 'No se encontraron usuarios con ese criterio' : 'No hay usuarios registrados'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-100">
                <tr className="border-b-2 border-gray-300">
                  <th className="px-3 py-3 text-center font-semibold border">No.</th>
                  <th className="px-3 py-3 text-center font-semibold border">Usuario</th>
                  <th className="px-3 py-3 text-center font-semibold border">Nombre y Apellidos</th>
                  <th className="px-3 py-3 text-center font-semibold border">Correo Electrónico</th>
                  <th className="px-3 py-3 text-center font-semibold border">Rol</th>
                  <th className="px-3 py-3 text-center font-semibold border">Estado</th>
                  <th className="px-3 py-3 text-center font-semibold border">Actualización</th>
                  <th className="px-3 py-3 text-center font-semibold border">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsuarios.map((usuario, index) => (
                  <tr
                    key={usuario.id_usuario}
                    className="border-b border-gray-200 hover:bg-gray-50 transition"
                  >
                    <td className="px-3 py-2 text-center border">{index + 1}</td>
                    <td className="px-3 py-2 border">{usuario.usuario}</td>
                    <td className="px-3 py-2 border">{usuario.nombre_apellidos}</td>
                    <td className="px-3 py-2 border">{usuario.email || <span className="text-gray-400 italic">No asignado</span>}</td>
                    <td className="px-3 py-2 border">
                      <div className="flex flex-col items-center">
                        <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium w-fit">
                          {usuario.rol}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-center border">
                      <button
                        onClick={() => handleToggleEstado(usuario)}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-200 transition"
                        title={usuario.estado === 'activo' ? 'Habilitado - Click para inhabilitar' : 'Inhabilitado - Click para habilitar'}
                      >
                        <Check
                          size={20}
                          className={usuario.estado === 'activo' ? 'text-green-600' : 'text-gray-400'}
                        />
                      </button>
                    </td>
                    <td className="px-3 py-2 border">
                      {usuario.usuario_actualizacion && usuario.fecha_actualizacion ? (
                        <div>
                          <div>{usuario.usuario_actualizacion}</div>
                          <div className="text-xs text-gray-500">{formatFecha(usuario.fecha_actualizacion)}</div>
                        </div>
                      ) : (
                        <span className="text-gray-400">N/A</span>
                      )}
                    </td>
                    <td className="px-4 py-4 border">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleOpenModal(usuario)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded transition"
                          title="Editar usuario"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(usuario)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded transition"
                          title="Eliminar usuario"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingUser ? 'Editar Usuario' : 'Añadir Usuario'}
              </h2>
              <button onClick={handleCloseModal} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Usuario <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.usuario}
                  onChange={(e) => setFormData({ ...formData, usuario: e.target.value })}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none transition
                    ${formErrors.usuario ? 'border-red-500' : 'border-gray-300 focus:border-blue-500'}`}
                  placeholder="ej: jdoe"
                />
                {formErrors.usuario && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.usuario}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre y Apellidos <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.nombre_apellidos}
                  onChange={(e) => setFormData({ ...formData, nombre_apellidos: e.target.value })}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none transition
                    ${formErrors.nombre_apellidos ? 'border-red-500' : 'border-gray-300 focus:border-blue-500'}`}
                  placeholder="ej: Juan Pérez"
                />
                {formErrors.nombre_apellidos && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.nombre_apellidos}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Correo Electrónico <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none transition
                    ${formErrors.email ? 'border-red-500' : 'border-gray-300 focus:border-blue-500'}`}
                  placeholder="ej: usuario@ebid.edu.bo"
                />
                {formErrors.email && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contraseña {!editingUser && <span className="text-red-500">*</span>}
                  {editingUser && <span className="text-gray-500 text-xs ml-1">(dejar en blanco para no cambiar)</span>}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className={`w-full px-4 py-2 pr-12 border rounded-lg focus:outline-none transition
                      ${formErrors.password ? 'border-red-500' : 'border-gray-300 focus:border-blue-500'}`}
                    placeholder="Mínimo 8 caracteres"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {formErrors.password && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.password}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rol <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.id_rol}
                  onChange={(e) => setFormData({ ...formData, id_rol: e.target.value })}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none transition
                    ${formErrors.id_rol ? 'border-red-500' : 'border-gray-300 focus:border-blue-500'}`}
                >
                  <option value="">Selecciona un rol</option>
                  {roles.map(rol => (
                    <option key={rol.id_rol} value={rol.id_rol} className="capitalize">
                      {rol.nombre.charAt(0).toUpperCase() + rol.nombre.slice(1).toLowerCase()}
                    </option>
                  ))}
                </select>
                {formErrors.id_rol && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.id_rol}</p>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-[#1e3a8a] text-white rounded-lg hover:bg-[#2c4a9a] transition disabled:opacity-50"
                >
                  {loading ? 'Guardando...' : (editingUser ? 'Actualizar' : 'Crear Usuario')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestionUsuarios;